import { useWorkspaceAsset } from '@gripp/shared-logic';
import {
  extractAndDeduplicateRoutines,
  RoutineButtons,
  RoutineExecutionProps,
  SlideAction,
  tokens,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAssetRouting } from '../assetRouteContext';
import { isActivityOrEditPage, RoutineSubmitSource } from '../assetUtils';
import { ViewAllSectionHeader } from './viewAllSectionHeader';

const INITIAL_ROUTINES_TO_SHOW = 3;

export const AssetRoutineFold: React.FC<{
  assetId: string;
  forceShowing?: boolean;
}> = ({ assetId, forceShowing }) => {
  const slideLayoutContext = useSlideLayoutSafe();
  const navigate = useNavigate();
  const location = useLocation();

  const { workspaceAsset } = useWorkspaceAsset(assetId);
  const { t } = useTranslation();
  // basePath and issueId come from the nearest AssetRouteProvider, which is
  // set by every component that renders AssetCard (both left and right columns).
  const { basePath, issueId } = useAssetRouting(assetId);
  const skipNextSlide = isActivityOrEditPage(location);

  const onRoutine = useCallback(
    async (routine: RoutineExecutionProps) => {
      if (slideLayoutContext && !skipNextSlide) {
        await slideLayoutContext.onSlideChange(SlideAction.NEXT);
      }

      const routineUrl = `${basePath}/routines/${routine.routine?.id}?from=${RoutineSubmitSource.AssetCard}`;

      navigate(routineUrl);
    },
    [assetId, basePath, navigate, slideLayoutContext, skipNextSlide]
  );

  const isOnAssetPage = location.pathname.startsWith('/assets/');

  if (!workspaceAsset || (!forceShowing && issueId && isOnAssetPage))
    return null;

  const handleViewAllRoutines = async () => {
    if (!skipNextSlide && slideLayoutContext) {
      await slideLayoutContext.onSlideChange(SlideAction.NEXT);
    }

    navigate(`${basePath}/routines/all`);
  };

  const hasRoutines =
    (workspaceAsset.routines?.length ?? 0) > 0 ||
    extractAndDeduplicateRoutines(workspaceAsset).length > 0;

  return (
    <View style={styles.containerHeader}>
      <ViewAllSectionHeader
        title={t('assets.routines')}
        onPress={handleViewAllRoutines}
        disabled={!hasRoutines}
      />
      <RoutineButtons
        workspaceAsset={workspaceAsset}
        initialRoutinesToShow={INITIAL_ROUTINES_TO_SHOW}
        showAll={false}
        onRoutineSelected={onRoutine}
        onNavigateToRoutineEdit={async () => {
          if (!skipNextSlide && slideLayoutContext) {
            await slideLayoutContext.onSlideChange(SlideAction.NEXT);
          }
          navigate(
            `${basePath}/routines/edit`,
            { state: { from: RoutineSubmitSource.AssetCard } }
          );
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  containerHeader: {
    marginBottom: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.sm,
  },
});
